import {
  AlertTriangle,
  ArrowDown,
  ArrowRightLeft,
  Bot,
  Check,
  Cloud,
  Code,
  Copy,
  Download,
  Loader2,
  Paperclip,
  RotateCcw,
  User,
  Wrench,
} from "lucide-react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { HandoffDivider } from "@/components/chat/AgentHandoff";
import { ArtifactCard } from "@/components/chat/ArtifactCard";
import { HtmlArtifactCard } from "@/components/chat/HtmlArtifactCard";
import { ImageAttachmentThumbnail } from "@/components/chat/ImageAttachmentThumbnail";
import { ReasoningTrace } from "@/components/chat/ReasoningTrace";
import { SourcesPanel } from "@/components/chat/SourcesPanel";
import { Markdown } from "@/components/shared/Markdown";
import { AskUserCard } from "@/components/tasks/AskUserCard";
import { ALLOWED_IMAGE_TYPES } from "@/constants/files";
import { useAuth } from "@/context/AuthContext";
import type { StreamingArtifact } from "@/hooks/useChatStreaming";
import { handoffOf } from "@/lib/agentHandoff";
import type { ChatMessage, ReasoningStep, SourceMetadata } from "@/types";
import type { AskUserQuestion } from "@/types/task";

/** Human-readable labels and icons for tool execution indicators. */
const TOOL_LABELS: Record<string, { label: string; icon: typeof Code }> = {
  dynamic_session: { label: "Running code...", icon: Code },
  web_search: { label: "Searching the web...", icon: Wrench },
  fetch_sharepoint_document: { label: "Fetching document...", icon: Wrench },
  generate_html: { label: "Generating HTML document...", icon: Wrench },
  request_extension_access: { label: "Checking your extension settings...", icon: Wrench },
  handoff_to_agent: { label: "Handing off to another agent...", icon: ArrowRightLeft },
};

function getToolLabel(name: string): { label: string; icon: typeof Code } {
  return TOOL_LABELS[name] ?? { label: `Running ${name}...`, icon: Wrench };
}

/** Resolves the display name for a message sender. */
function MessageSenderName({
  message,
  sessionOwnerName,
}: Readonly<{
  message: ChatMessage;
  sessionOwnerName?: string;
}>) {
  const { profile } = useAuth();

  if (message.role !== "user") {
    return <span className="font-semibold text-content-primary">AI Assistant</span>;
  }

  const name = message.senderName || sessionOwnerName || profile?.user?.displayName || profile?.user?.name || "You";

  return <span className="font-semibold text-content-primary">{name}</span>;
}

/** Avatar icon for a message (user, assistant, or error). */
function MessageAvatar({ message, isError }: Readonly<{ message: ChatMessage; isError: boolean }>) {
  if (message.role === "user") {
    return (
      <div className="w-8 h-8 bg-linear-to-br from-slate-600 to-slate-700 rounded-lg flex items-center justify-center shrink-0">
        <User className="w-5 h-5 text-white" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="w-8 h-8 bg-amber-500/20 border border-amber-500/30 rounded-lg flex items-center justify-center shrink-0">
        <AlertTriangle className="w-5 h-5 text-amber-400" />
      </div>
    );
  }
  return (
    <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
      <Bot className="w-5 h-5 text-white" />
    </div>
  );
}

/** Copy and regenerate action buttons for assistant messages. */
function MessageActions({
  message,
  copiedId,
  onCopy,
  onRegenerate,
  isStreaming,
}: Readonly<{
  message: ChatMessage;
  copiedId: number | null;
  onCopy: (content: string, id: number) => void;
  onRegenerate: () => void;
  isStreaming: boolean;
}>) {
  return (
    <div className="flex items-center gap-2 mt-3">
      <button
        onClick={() => onCopy(message.content, message.id)}
        className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary"
        title="Copy"
      >
        {copiedId === message.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
      </button>
      <button
        onClick={onRegenerate}
        disabled={isStreaming}
        className="p-2 hover:bg-interactive-hover rounded-lg transition text-content-tertiary hover:text-content-primary disabled:opacity-50 disabled:cursor-not-allowed"
        title="Regenerate"
      >
        <RotateCcw className="w-4 h-4" />
      </button>
    </div>
  );
}

/** Renders the text/markdown body of a message. */
function MessageContent({ message, isError }: Readonly<{ message: ChatMessage; isError: boolean }>) {
  if (isError) {
    return (
      <div className="px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-800 dark:text-amber-200 text-sm">
        {message.content}
      </div>
    );
  }
  if (message.role === "assistant") {
    return <Markdown content={message.content} />;
  }
  return <div className="text-content-secondary whitespace-pre-wrap wrap-break-word">{message.content}</div>;
}

/** Attachments displayed below a message (SharePoint docs, images, files, artifacts). */
function MessageAttachments({ message, isError }: Readonly<{ message: ChatMessage; isError: boolean }>) {
  return (
    <>
      {/* SharePoint document chips for user messages */}
      {message.role === "user" &&
        message.messageMetadata?.sharepoint_documents &&
        message.messageMetadata.sharepoint_documents.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.messageMetadata.sharepoint_documents.map((doc) => (
              <a
                key={doc.url}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 rounded-lg text-xs text-blue-400 hover:bg-blue-500/20 transition"
                title={doc.url}
              >
                <Cloud className="w-3.5 h-3.5" />
                <span className="max-w-[200px] truncate">{doc.filename}</span>
                {doc.size != null &&
                  (() => {
                    // NOSONAR — deeply nested React JSX pattern
                    const formatSize = (size: number) => {
                      if (size < 1024) return `${size} B`;
                      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
                      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
                    };
                    return <span className="text-blue-500/60">{formatSize(doc.size)}</span>;
                  })()}
              </a>
            ))}
          </div>
        )}
      {/* File/image attachments for user messages */}
      {message.role === "user" && message.attachments && message.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {message.attachments
            .filter((att) => ALLOWED_IMAGE_TYPES.has(att.mimeType))
            .map((att) => (
              <ImageAttachmentThumbnail key={att.uuid} attachment={att} />
            ))}
          {message.attachments
            .filter((att) => !ALLOWED_IMAGE_TYPES.has(att.mimeType))
            .map((att) => (
              <div
                key={att.uuid}
                className="flex items-center gap-2 bg-surface-secondary pl-3 pr-3 py-2 rounded-lg border border-border-secondary"
              >
                <Paperclip className="w-3.5 h-3.5 text-content-tertiary shrink-0" />
                <span className="text-sm text-content-secondary truncate max-w-[200px]">{att.filename}</span>
              </div>
            ))}
        </div>
      )}
      {/* Image attachments on assistant messages — only show images
      that are NOT already rendered inline in the markdown content. */}
      {message.role === "assistant" &&
        message.attachments &&
        message.attachments.length > 0 &&
        (() => {
          const imageAtts = message.attachments!.filter(
            (att) =>
              ALLOWED_IMAGE_TYPES.has(att.mimeType) &&
              !message.content.includes(`/chat/attachments/${att.uuid}/file`) &&
              !new RegExp(
                String.raw`!?\[[^\]]*\]\([^)]*${att.filename.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)}[^)]*\)`, // NOSONAR — complex regex escapes special chars in filename to match markdown image syntax
              ).test(message.content),
          );
          return imageAtts.length > 0 ? (
            <div className="flex flex-wrap gap-3 mt-3">
              {imageAtts.map((att) => (
                <ImageAttachmentThumbnail key={att.uuid} attachment={att} variant="generated" />
              ))}
            </div>
          ) : null;
        })()}
      {/* HTML document artifact cards on assistant messages */}
      {message.role === "assistant" &&
        !isError &&
        message.attachments?.some((att) => att.mimeType === "text/html" && att.origin === "generated") &&
        message.attachments
          .filter((att) => att.mimeType === "text/html" && att.origin === "generated")
          .map((att) => (
            <HtmlArtifactCard
              key={att.uuid}
              artifact={{
                filename: att.filename,
                url: att.url || `/chat/attachments/${att.uuid}/file`,
                is_image: false,
                mime_type: att.mimeType,
                attachment_uuid: att.uuid,
                version: att.version ?? undefined,
                lineage_uuid: att.lineageUuid ?? undefined,
                is_revision: att.isRevision,
                is_html: true,
                title: att.filename.replace(/\.html$/, ""),
              }}
            />
          ))}
      {/* Non-HTML generated file artifact cards */}
      {message.role === "assistant" &&
        !isError &&
        message.attachments
          ?.filter(
            (att) =>
              att.origin === "generated" && att.mimeType !== "text/html" && !ALLOWED_IMAGE_TYPES.has(att.mimeType),
          )
          .map((att) => (
            <ArtifactCard
              key={att.uuid}
              artifact={{
                filename: att.filename,
                url: att.url || `/chat/attachments/${att.uuid}/file`,
                is_image: false,
                mime_type: att.mimeType,
                attachment_uuid: att.uuid,
                version: att.version ?? undefined,
                lineage_uuid: att.lineageUuid ?? undefined,
                is_revision: att.isRevision,
              }}
            />
          ))}
      {/* Reasoning trace */}
      {message.role === "assistant" &&
        !isError &&
        ((message.reasoningSteps && message.reasoningSteps.length > 0) ||
          message.messageMetadata?.reasoning_summary) && (
          <ReasoningTrace steps={message.reasoningSteps ?? []} summary={message.messageMetadata?.reasoning_summary} />
        )}
      {/* Sources panel */}
      {message.role === "assistant" &&
        !isError &&
        message.messageMetadata?.sources &&
        message.messageMetadata.sources.length > 0 && <SourcesPanel sources={message.messageMetadata.sources} />}
    </>
  );
}

/**
 * Props for the ChatMessageList component.
 *
 * Accepts minimal slices of hook state so the component stays loosely coupled.
 */
interface ChatMessageListProps {
  messages: ChatMessage[];
  isLoading: boolean;
  /** Streaming state. */
  streaming: {
    isStreaming: boolean;
    streamingContent: string;
    activeToolName: string | null;
    streamingSources: SourceMetadata[];
    streamingReasoning: ReasoningStep[];
    streamingReasoningSummary?: string;
    streamingHtmlArtifacts: StreamingArtifact[];
    streamingFileArtifacts: StreamingArtifact[];
    askUserQuestions: AskUserQuestion[] | null;
    handleRegenerate: (messageIndex: number) => void;
    onAskUserSubmit: (text: string) => void;
  };
  /** Whether SharePoint files are currently being downloaded. */
  isDownloadingFiles: boolean;
  /** Name of the session owner (for shared chats, shows original author). */
  sessionOwnerName?: string;
  /** Rendered right after the first user message (the auto-routing row). */
  afterFirstUserMessage?: React.ReactNode;
  /** Rendered after the last message (the router's handoff suggestion). */
  afterMessages?: React.ReactNode;
}

/**
 * Message list area — loading spinner, empty state, message bubbles,
 * streaming indicator, and scroll-to-bottom behaviour.
 *
 * Extracted from Chat.tsx to reduce the monolith.
 */
export function ChatMessageList({
  messages,
  isLoading,
  streaming,
  isDownloadingFiles,
  sessionOwnerName,
  afterFirstUserMessage,
  afterMessages,
}: Readonly<ChatMessageListProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);

  // Track whether the user has scrolled up away from the bottom
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    const threshold = 100; // px from bottom considered "near bottom"
    const atBottom = container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
    setIsNearBottom(atBottom);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Auto-scroll only when user is near the bottom (not reading history)
  useEffect(() => {
    const container = containerRef.current;
    if (container && isNearBottom) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, streaming.streamingContent, isNearBottom]);

  const scrollToBottom = useCallback(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, []);

  const handleCopy = async (content: string, messageId: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(messageId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const getContentView = () => {
    if (isLoading) return "loading";
    if (messages.length === 0 && !streaming.streamingContent) return "empty";
    return "messages";
  };
  const contentView = getContentView();
  const firstUserIndex = messages.findIndex((m) => m.role === "user");

  return (
    <div className="relative flex-1 overflow-hidden">
      <div ref={containerRef} className="h-full overflow-y-auto p-6 space-y-6">
        {contentView === "loading" && (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin" />
          </div>
        )}
        {contentView === "empty" && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 bg-brand-gradient-br rounded-2xl flex items-center justify-center mb-4">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-content-primary mb-2">Start a conversation</h2>
            <p className="text-content-tertiary max-w-md">
              Select a model and optionally an agent, then type your message below to begin chatting.
            </p>
          </div>
        )}
        {contentView === "messages" && (
          <>
            {messages.map((message, index) => {
              const handoff = handoffOf(message);
              if (handoff) {
                return <HandoffDivider key={message.uuid ?? `handoff-${index}`} handoff={handoff} />;
              }
              const isError = !!message.uuid?.startsWith("error-");
              return (
                <Fragment key={message.uuid ?? `msg-${message.id}-${index}`}>
                  <div className="flex gap-4">
                    <MessageAvatar message={message} isError={isError} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSenderName message={message} sessionOwnerName={sessionOwnerName} />
                        <span className="text-xs text-content-quaternary">
                          {new Date(message.createdAt).toLocaleTimeString()}
                        </span>
                      </div>
                      <MessageContent message={message} isError={isError} />
                      <MessageAttachments message={message} isError={isError} />
                      {message.role === "assistant" && !isError && (
                        <MessageActions
                          message={message}
                          copiedId={copiedId}
                          onCopy={handleCopy}
                          onRegenerate={() => streaming.handleRegenerate(index)}
                          isStreaming={streaming.isStreaming}
                        />
                      )}
                    </div>
                  </div>
                  {index === firstUserIndex && afterFirstUserMessage}
                </Fragment>
              );
            })}
            {afterMessages}

            {/* Downloading SharePoint files indicator */}
            {isDownloadingFiles && (
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-blue-500/20 border border-blue-500/30 rounded-lg flex items-center justify-center shrink-0">
                  <Download className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                    <span className="text-sm text-blue-400">Downloading SharePoint documents...</span>
                  </div>
                </div>
              </div>
            )}

            {/* Tool execution indicator */}
            {streaming.isStreaming && streaming.activeToolName && (
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
                  {(() => {
                    const { icon: ToolIcon } = getToolLabel(streaming.activeToolName);
                    return <ToolIcon className="w-5 h-5 text-white" />;
                  })()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 text-brand-primary animate-spin" />
                    <span className="text-sm text-content-tertiary">
                      {getToolLabel(streaming.activeToolName).label}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Streaming sources indicator — shows sources found so far */}
            {streaming.isStreaming && streaming.streamingSources.length > 0 && (
              <div className="flex gap-4">
                <div className="w-8 h-8 shrink-0" /> {/* spacer aligned with avatar */}
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-content-quaternary">
                    <span className="text-content-tertiary font-medium">
                      {streaming.streamingSources.length} source{streaming.streamingSources.length !== 1 ? "s" : ""}{" "}
                      found:
                    </span>
                    {streaming.streamingSources.slice(0, 5).map((src, i) => {
                      const getSourceColorClass = () => {
                        if (src.type === "web") return "bg-blue-500/10 text-blue-400";
                        if (src.type === "sharepoint") return "bg-purple-500/10 text-purple-400";
                        return "bg-green-500/10 text-green-400";
                      };
                      const sourceColorClass = getSourceColorClass();
                      return (
                        <span
                          key={`ss-${src.name}-${i}`}
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] ${sourceColorClass}`}
                        >
                          {src.name.length > 30 ? `${src.name.slice(0, 30)}...` : src.name}
                        </span>
                      );
                    })}
                    {streaming.streamingSources.length > 5 && (
                      <span className="text-content-quaternary">+{streaming.streamingSources.length - 5} more</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Streaming message */}
            {streaming.isStreaming && (
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-content-primary">AI Assistant</span>
                  </div>
                  {streaming.streamingContent ? (
                    <div className="relative">
                      <Markdown content={streaming.streamingContent} isStreaming />
                      <span className="inline-block w-2 h-4 bg-brand-primary ml-1 animate-pulse" />
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-brand-primary rounded-full animate-bounce" />
                      <div
                        className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                        style={{ animationDelay: "0.2s" }}
                      />
                      <div
                        className="w-2 h-2 bg-brand-primary rounded-full animate-bounce"
                        style={{ animationDelay: "0.4s" }}
                      />
                    </div>
                  )}
                  {/* HTML artifact cards generated during this stream */}
                  {streaming.streamingHtmlArtifacts.length > 0 &&
                    streaming.streamingHtmlArtifacts.map((art, i) => (
                      <HtmlArtifactCard key={`html-art-${art.attachment_uuid || i}`} artifact={art} />
                    ))}
                  {/* Non-HTML generated file artifact cards during this stream */}
                  {streaming.streamingFileArtifacts.length > 0 &&
                    streaming.streamingFileArtifacts.map((art, i) => (
                      <ArtifactCard key={`file-art-${art.attachment_uuid || i}`} artifact={art} />
                    ))}
                  {/* Reasoning trace for streaming message — shows the tool calls and summary collected so far */}
                  {(streaming.streamingReasoning.length > 0 || streaming.streamingReasoningSummary) && (
                    <ReasoningTrace
                      steps={streaming.streamingReasoning}
                      summary={streaming.streamingReasoningSummary}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Ask-user structured question card (chat mode) */}
            {streaming.askUserQuestions && streaming.askUserQuestions.length > 0 && (
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-brand-gradient-br rounded-lg flex items-center justify-center shrink-0">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <AskUserCard
                    questions={streaming.askUserQuestions}
                    onSubmitLegacy={(text) => streaming.onAskUserSubmit(text)}
                    onSubmitStructured={(responses) => {
                      const parts = responses.map((r) => {
                        const answer = r.selected.length > 0 ? r.selected.join(", ") : (r.customText ?? "");
                        return `${r.header}: ${answer}`;
                      });
                      streaming.onAskUserSubmit(parts.join("\n"));
                    }}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {/* Scroll to bottom button — shown when user scrolls up */}
      {!isNearBottom && contentView === "messages" && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-full shadow-lg transition text-sm text-content-primary"
        >
          <ArrowDown className="w-4 h-4" />
          <span>Scroll to bottom</span>
        </button>
      )}
    </div>
  );
}
